import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, Sparkle, Warning, X } from '@phosphor-icons/react';

import { listTeams } from '@/api/teams';
import { listLocations } from '@/api/locations';
import { getSchedulingRulesSummary } from '@/api/teamCategories';
import {
  addSlot,
  cancelTemplateDraftJob,
  createTemplate,
  enqueueTemplateDraft,
  getTemplateDraftJob,
  listTemplates,
  type DraftJobState,
  type DraftMode,
  type TemplateDraft,
} from '@/api/templates';
import { subscribeToJob } from '@/hooks/useNotificationsStream';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TablePagination } from '@/components/ui/table-pagination';

// TemplateGenerateDialog drives the AI template-generation flow:
//
//   pick teams + mode + name  →  loading (POST /templates/draft)  →
//   preview + summary + warnings  →  Create (POST /templates + /slots,
//   or AddSlot if append mode)
//
// State machine is local. The dialog is centered modal (matches
// TeamModal). On Create success we invalidate the templates list and
// close.

interface Props {
  open: boolean;
  onClose: () => void;
  slug: string;
  // When set, the dialog locks to append-mode against this template —
  // mode picker + template selector are hidden, and the prompt reads
  // "Adding to <template name>". Opened from inside a template editor.
  forceTemplateId?: string;
  // Called with the created or appended template's id when the user
  // finishes. The Schedule page navigates to that template's editor so
  // the admin can fine-tune.
  onCreated?: (templateId: string) => void;
  // When set, the dialog opens directly into the polling/loading step
  // for an in-flight (or just-completed) draft job. Used by the bell
  // deep-link click-through.
  resumeJobId?: string;
}

type Step = 'configure' | 'loading' | 'preview' | 'creating';

export function TemplateGenerateDialog({ open, onClose, slug, forceTemplateId, onCreated, resumeJobId }: Props) {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('configure');
  const [mode, setMode] = useState<DraftMode>('new');
  const [templateName, setTemplateName] = useState('');
  const [templateID, setTemplateID] = useState('');
  const [selectedTeamIDs, setSelectedTeamIDs] = useState<Set<string>>(new Set());
  // Location filter is opt-in: empty set = "all locations" (same as
  // omitting the field on the request). Once the user picks at least one,
  // the AI is told only about those.
  const [selectedLocationIDs, setSelectedLocationIDs] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  // jobID holds the in-flight or completed draft job. While it's set and
  // status is pending/running, the polling query below ticks every 2s.
  const [jobID, setJobID] = useState<string | null>(null);

  // Server-side team search. The backend's defaultListLimit is 25 and the
  // hard max is 200, so we always page at 200 and rely on search to find
  // anything beyond that. 250ms debounce matches the Teams page.
  const [teamSearch, setTeamSearch] = useState('');
  const [debouncedTeamSearch, setDebouncedTeamSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedTeamSearch(teamSearch.trim()), 250);
    return () => clearTimeout(id);
  }, [teamSearch]);

  const teamsQuery = useQuery({
    queryKey: ['teams', 'template-generate', slug, debouncedTeamSearch],
    queryFn: () =>
      listTeams(token, slug, { limit: 200, search: debouncedTeamSearch }),
    enabled: !!token && !!slug && open,
  });
  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data]);
  const teamsTotal = teamsQuery.data?.total ?? 0;

  const templatesQuery = useQuery({
    queryKey: ['templates', slug],
    queryFn: () => listTemplates(token, slug),
    // Also active when forceTemplateId is set so we can show the locked
    // template's name in the header without waiting for a mode flip.
    enabled: !!token && !!slug && open && (mode === 'append' || !!forceTemplateId),
  });
  const templates = useMemo(
    () => templatesQuery.data ?? [],
    [templatesQuery.data],
  );

  // Per-team "has any scheduling rule" flag. The picker disables teams
  // without rules upfront — the draft service would exclude them anyway,
  // so there's no point letting the user pick them and then surface a
  // warning in the preview. Queried only while the dialog is open.
  const rulesSummaryQuery = useQuery({
    queryKey: ['team-scheduling-rules-summary', slug],
    queryFn: () => getSchedulingRulesSummary(token, slug),
    enabled: !!token && !!slug && open,
  });
  const teamsWithRules = useMemo(() => {
    const set = new Set<string>();
    for (const row of rulesSummaryQuery.data?.teams ?? []) {
      if (row.has_rules) set.add(row.team_id);
    }
    return set;
  }, [rulesSummaryQuery.data]);

  const locationsQuery = useQuery({
    queryKey: ['locations', 'template-generate', slug],
    queryFn: () => listLocations(token, slug, { limit: 200 }),
    enabled: !!token && !!slug && open,
  });
  const locations = useMemo(
    () => locationsQuery.data?.locations ?? [],
    [locationsQuery.data],
  );

  // Reset on open. When forceTemplateId is set (opened from the template
  // editor) we lock to append-mode against that template. When
  // resumeJobId is set (bell deep-link), we jump straight to the loading
  // step and let the polling query reconcile state.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      if (resumeJobId) {
        setStep('loading');
        setJobID(resumeJobId);
      } else {
        setStep('configure');
        setJobID(null);
      }
      if (forceTemplateId) {
        setMode('append');
        setTemplateID(forceTemplateId);
      } else {
        setMode('new');
        setTemplateID('');
      }
      setTemplateName('');
      setSelectedTeamIDs(new Set());
      setSelectedLocationIDs(new Set());
      setTeamSearch('');
      setDebouncedTeamSearch('');
      setDraft(null);
      setError(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open, forceTemplateId, resumeJobId]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handle = () => onClose();
    el.addEventListener('close', handle);
    return () => el.removeEventListener('close', handle);
  }, [onClose]);

  // Submit a new draft job. POST returns 202 + job_id; we move to the
  // loading step and let the polling query below drive the rest of the
  // state machine.
  const enqueueMutation = useMutation({
    mutationFn: () =>
      enqueueTemplateDraft(token, slug, {
        team_ids: Array.from(selectedTeamIDs),
        mode,
        ...(mode === 'append' ? { template_id: templateID } : {}),
        ...(mode === 'new' ? { template_name: templateName.trim() } : {}),
        ...(selectedLocationIDs.size > 0
          ? { location_ids: Array.from(selectedLocationIDs) }
          : {}),
        locale: i18n.language || 'en',
      }),
    onSuccess: (resp) => {
      setJobID(resp.job_id);
      setStep('loading');
    },
    onError: (err) => {
      setStep('configure');
      setError(err instanceof Error ? err.message : 'unknown');
    },
  });

  // The job's live state comes primarily through the SSE subscription set
  // up below — progress ticks merge into this query's cache directly, and
  // a terminal status triggers a single refetch to pull the .draft
  // payload. The slow refetchInterval is a safety net for any dropped
  // event (flaky network, hub buffer drop), same discipline the bell uses.
  const jobQuery = useQuery({
    queryKey: ['templateDraftJob', slug, jobID],
    queryFn: () => getTemplateDraftJob(token, slug, jobID!),
    enabled: !!token && !!slug && !!jobID && open,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 30_000;
      return data.status === 'pending' || data.status === 'running' ? 30_000 : false;
    },
    refetchOnWindowFocus: false,
  });

  // Live progress via SSE. The hook merges incoming `job_progress` events
  // into the same query cache the dialog renders from, so the progress
  // bar updates within ~100ms of the worker writing a batch. On terminal
  // status we trigger a refetch to pull the `draft` payload (which isn't
  // sent over SSE — it can be tens of KB and pg_notify has an 8KB cap).
  useEffect(() => {
    if (!jobID || !open) return;
    const queryKey = ['templateDraftJob', slug, jobID] as const;
    return subscribeToJob(jobID, (ev) => {
      queryClient.setQueryData<DraftJobState>(queryKey, (prev) => {
        // Map the SSE shape (done/total) onto the existing DraftJobState
        // shape (batches_done/batches_total). Preserve everything else
        // already in the cache — most importantly the .draft field that
        // arrives via the terminal-status refetch.
        const next: DraftJobState = prev
          ? { ...prev, status: ev.status }
          : {
              job_id: ev.job_id,
              status: ev.status,
              progress: { batches_total: 0, batches_done: 0, slots_emitted: 0 },
              created_at: ev.updated_at,
            };
        next.progress = {
          ...next.progress,
          batches_total: ev.progress.total,
          batches_done: ev.progress.done,
        };
        return next;
      });
      if (ev.status === 'completed' || ev.status === 'failed' || ev.status === 'cancelled') {
        void jobQuery.refetch();
      }
    });
  }, [jobID, open, slug, queryClient, jobQuery]);

  // Reconcile polling result with the dialog step.
  useEffect(() => {
    if (!jobQuery.data) return;
    const job = jobQuery.data;
    if (job.status === 'completed' && job.draft) {
      setDraft(job.draft);
      setStep('preview');
    } else if (job.status === 'failed') {
      setError(job.error_message || 'draft_failed');
      setStep('configure');
      setJobID(null);
    } else if (job.status === 'cancelled') {
      // Cancelled rows come back here when the user resumed via the bell
      // after the worker had already wrapped up — silently return to
      // configure rather than show an error.
      setStep('configure');
      setJobID(null);
    }
  }, [jobQuery.data]);

  // Cancel the running job. We don't wait for the result — the polling
  // query will see status='cancelled' on its next tick and update step.
  const cancelJobMutation = useMutation({
    mutationFn: () => {
      if (!jobID) throw new Error('no job');
      return cancelTemplateDraftJob(token, slug, jobID);
    },
    onSuccess: () => {
      setJobID(null);
      setStep('configure');
    },
  });

  const createMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      if (!draft) throw new Error('no draft');
      // New mode → create the template first, then add each slot in
      // order. Append mode → reuse the existing template_id.
      const targetID =
        draft.template_id ??
        (await createTemplate(token, slug, {
          name: draft.template_name ?? templateName.trim(),
        })).id;
      // addSlot is sequential because there's no batch endpoint today
      // and the slot count is bounded (<80) — total network time is
      // bounded by the round-trip × slot-count, which is acceptable.
      for (const s of draft.slots) {
        await addSlot(token, slug, targetID, {
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
          title: s.title,
          kind: s.kind,
          ...(s.description ? { description: s.description } : {}),
          ...(s.location_id ? { location_id: s.location_id } : {}),
          ...(s.zone_id ? { zone_id: s.zone_id } : {}),
          team_id: s.team_id,
        });
      }
      return targetID;
    },
    onSuccess: (tid) => {
      void queryClient.invalidateQueries({ queryKey: ['templates', slug] });
      onCreated?.(tid);
      onClose();
    },
    onError: (err) => {
      setStep('preview');
      setError(err instanceof Error ? err.message : 'unknown');
    },
  });

  const canGenerate = (() => {
    if (selectedTeamIDs.size === 0) return false;
    if (mode === 'new' && templateName.trim().length === 0) return false;
    if (mode === 'append' && !templateID) return false;
    return !enqueueMutation.isPending;
  })();

  function toggleTeam(id: string) {
    // Defensive guard — the row is already disabled in the UI when a
    // team has no rules, but block here too so a stale click in flight
    // can't slip an excluded team into the selection.
    if (rulesSummaryQuery.isSuccess && !teamsWithRules.has(id)) return;
    setSelectedTeamIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Select-all only acts on teams that have rules. With the summary
  // unloaded we fall back to "every team" since we don't yet know which
  // to exclude — the toggleTeam guard above still backstops bad cases.
  function toggleSelectAllTeams() {
    const eligibleIDs = teams
      .filter((tm) => !rulesSummaryQuery.isSuccess || teamsWithRules.has(tm.id))
      .map((tm) => tm.id);
    const allSelected =
      eligibleIDs.length > 0 && eligibleIDs.every((id) => selectedTeamIDs.has(id));
    setSelectedTeamIDs(allSelected ? new Set() : new Set(eligibleIDs));
  }

  function toggleLocation(id: string) {
    setSelectedLocationIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Empty set already means "all locations" to the backend, but the visible
  // checkboxes follow the explicit set — so select-all fills every box, and
  // deselect-all clears back to the empty (== all) state.
  function toggleSelectAllLocations() {
    const allSelected =
      locations.length > 0 && locations.every((loc) => selectedLocationIDs.has(loc.id));
    setSelectedLocationIDs(allSelected ? new Set() : new Set(locations.map((loc) => loc.id)));
  }

  function startGenerate() {
    setError(null);
    setStep('loading');
    enqueueMutation.mutate();
  }

  // "Continue in background": close the dialog without cancelling the job.
  // The bell will ping when the worker finishes (template_draft_completed
  // notification carries the job_id in metadata for deep-link reentry).
  function continueInBackground() {
    onClose();
  }

  function startCreate() {
    setError(null);
    setStep('creating');
    createMutation.mutate();
  }

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 text-foreground shadow-2xl backdrop:bg-foreground/40"
    >
      <div className="flex max-h-[85vh] w-[min(48rem,92vw)] flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-border p-6 pb-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-pill bg-primary/20">
              <Sparkle className="h-5 w-5 text-primary-foreground" weight="bold" />
            </span>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">
                {t('templateGenerate.title')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('templateGenerate.intro')}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
          {step === 'configure' && (
            <ConfigureStep
              slug={slug}
              teams={teams}
              teamsTotal={teamsTotal}
              teamSearch={teamSearch}
              setTeamSearch={setTeamSearch}
              templates={templates}
              mode={mode}
              setMode={setMode}
              templateName={templateName}
              setTemplateName={setTemplateName}
              templateID={templateID}
              setTemplateID={setTemplateID}
              selectedTeamIDs={selectedTeamIDs}
              toggleTeam={toggleTeam}
              toggleSelectAllTeams={toggleSelectAllTeams}
              dialogEl={dialogRef.current}
              lockedTemplateName={
                forceTemplateId
                  ? templates.find((tpl) => tpl.id === forceTemplateId)?.name ?? null
                  : null
              }
              teamsWithRules={teamsWithRules}
              rulesSummaryLoaded={rulesSummaryQuery.isSuccess}
              locations={locations}
              selectedLocationIDs={selectedLocationIDs}
              toggleLocation={toggleLocation}
              toggleSelectAllLocations={toggleSelectAllLocations}
            />
          )}
          {step === 'loading' && (
            <LoadingStep
              progress={jobQuery.data?.progress}
              status={jobQuery.data?.status}
            />
          )}
          {(step === 'preview' || step === 'creating') && draft && (
            <PreviewStep draft={draft} />
          )}
          {error && (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {t(`templateGenerate.error.${error}`, {
                defaultValue: t('templateGenerate.error.unknown'),
              })}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-background p-4">
          {step === 'configure' && (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="button" disabled={!canGenerate} onClick={startGenerate}>
                <Sparkle className="h-4 w-4" weight="bold" />
                {t('templateGenerate.generate')}
              </Button>
            </>
          )}
          {step === 'loading' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => cancelJobMutation.mutate()}
                disabled={!jobID || cancelJobMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={continueInBackground}
                disabled={!jobID}
              >
                {t('templateGenerate.continueInBackground')}
              </Button>
              <Button type="button" disabled>
                <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
                {t('templateGenerate.generating')}
              </Button>
            </>
          )}
          {step === 'preview' && draft && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('configure')}
              >
                {t('common.back')}
              </Button>
              <Button type="button" onClick={startCreate}>
                {mode === 'append'
                  ? t('templateGenerate.appendCta')
                  : t('templateGenerate.createCta')}
              </Button>
            </>
          )}
          {step === 'creating' && (
            <Button type="button" disabled>
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
              {t('templateGenerate.creating')}
            </Button>
          )}
        </footer>
      </div>
    </dialog>
  );
}

function ConfigureStep({
  slug,
  teams,
  teamsTotal,
  teamSearch,
  setTeamSearch,
  templates,
  mode,
  setMode,
  templateName,
  setTemplateName,
  templateID,
  setTemplateID,
  selectedTeamIDs,
  toggleTeam,
  toggleSelectAllTeams,
  dialogEl,
  lockedTemplateName,
  teamsWithRules,
  rulesSummaryLoaded,
  locations,
  selectedLocationIDs,
  toggleLocation,
  toggleSelectAllLocations,
}: {
  slug: string;
  teams: { id: string; name: string }[];
  teamsTotal: number;
  teamSearch: string;
  setTeamSearch: (s: string) => void;
  templates: { id: string; name: string }[];
  mode: DraftMode;
  setMode: (m: DraftMode) => void;
  templateName: string;
  setTemplateName: (s: string) => void;
  templateID: string;
  setTemplateID: (s: string) => void;
  selectedTeamIDs: Set<string>;
  toggleTeam: (id: string) => void;
  toggleSelectAllTeams: () => void;
  dialogEl: HTMLDialogElement | null;
  lockedTemplateName: string | null;
  teamsWithRules: Set<string>;
  rulesSummaryLoaded: boolean;
  locations: { id: string; name: string }[];
  selectedLocationIDs: Set<string>;
  toggleLocation: (id: string) => void;
  toggleSelectAllLocations: () => void;
}) {
  const { t } = useTranslation('common');

  const eligibleTeamCount = useMemo(() => {
    if (!rulesSummaryLoaded) return teams.length;
    return teams.filter((tm) => teamsWithRules.has(tm.id)).length;
  }, [teams, teamsWithRules, rulesSummaryLoaded]);
  const allEligibleSelected =
    eligibleTeamCount > 0 &&
    teams
      .filter((tm) => !rulesSummaryLoaded || teamsWithRules.has(tm.id))
      .every((tm) => selectedTeamIDs.has(tm.id));

  // Sort: teams with rules first (alphabetical), disabled teams below.
  // Stable secondary sort keeps the in-group order predictable. While
  // the rules summary is still loading we leave the original order so
  // rows don't shuffle once the data lands.
  const sortedTeams = useMemo(() => {
    if (!rulesSummaryLoaded) return teams;
    const score = (tm: { id: string }) => (teamsWithRules.has(tm.id) ? 0 : 1);
    return [...teams].sort((a, b) => {
      const diff = score(a) - score(b);
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name);
    });
  }, [teams, teamsWithRules, rulesSummaryLoaded]);
  return (
    <div className="flex flex-col gap-5">
      {lockedTemplateName !== null ? (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          <span className="text-muted-foreground">{t('templateGenerate.appendTo')}: </span>
          <span className="font-semibold text-foreground">{lockedTemplateName}</span>
        </div>
      ) : (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-foreground">
              {t('templateGenerate.mode.legend')}
            </legend>
            <div className="flex gap-2">
              <ModePill
                active={mode === 'new'}
                onClick={() => setMode('new')}
                label={t('templateGenerate.mode.new')}
              />
              <ModePill
                active={mode === 'append'}
                onClick={() => setMode('append')}
                label={t('templateGenerate.mode.append')}
              />
            </div>
          </fieldset>

          {mode === 'new' ? (
            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
              {t('templateGenerate.templateName')}
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('templateGenerate.templateNamePlaceholder')}
                maxLength={120}
                autoFocus
              />
            </label>
          ) : (
            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
              {t('templateGenerate.appendTo')}
              <Select value={templateID} onValueChange={setTemplateID}>
                <SelectTrigger>
                  <SelectValue placeholder={t('templateGenerate.appendToPlaceholder')} />
                </SelectTrigger>
                <SelectContent container={dialogEl}>
                  {templates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}
        </>
      )}

      <fieldset className="flex flex-col gap-2">
        <legend className="flex items-baseline justify-between gap-3 text-sm font-medium text-foreground">
          <span>{t('templateGenerate.teams.legend')}</span>
          <span className="flex items-center gap-3">
            {eligibleTeamCount > 0 && (
              <button
                type="button"
                onClick={toggleSelectAllTeams}
                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                {allEligibleSelected
                  ? t('templateGenerate.teams.deselectAll')
                  : t('templateGenerate.teams.selectAll')}
              </button>
            )}
            <span className="text-xs font-normal text-muted-foreground">
              {t('templateGenerate.teams.selectedCount', {
                count: selectedTeamIDs.size,
              })}
            </span>
          </span>
        </legend>
        <SearchInput
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          placeholder={t('templateGenerate.teams.searchPlaceholder')}
          aria-label={t('templateGenerate.teams.searchPlaceholder')}
          clearLabel={t('common.clearSearch')}
        />
        {teams.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            {teamSearch.trim().length > 0
              ? t('templateGenerate.teams.noMatch')
              : t('templateGenerate.teams.empty')}
          </p>
        ) : (
          <div className="grid max-h-72 grid-cols-1 gap-1 overflow-y-auto rounded-xl border border-border bg-muted/20 p-2 sm:grid-cols-2">
            {sortedTeams.map((tm) => {
              const disabled = rulesSummaryLoaded && !teamsWithRules.has(tm.id);
              return (
                <label
                  key={tm.id}
                  className={
                    disabled
                      ? 'flex cursor-not-allowed items-center gap-2 rounded-pill px-2 py-1.5 text-sm text-muted-foreground opacity-60'
                      : 'flex cursor-pointer items-center gap-2 rounded-pill px-2 py-1.5 text-sm text-foreground hover:bg-background'
                  }
                  title={disabled ? t('templateGenerate.teams.noRulesHint') : undefined}
                >
                  <Checkbox
                    checked={selectedTeamIDs.has(tm.id)}
                    onChange={() => toggleTeam(tm.id)}
                    disabled={disabled}
                  />
                  <span className="truncate">{tm.name}</span>
                  {disabled && (
                    <span className="ml-auto shrink-0 rounded-pill bg-muted px-2 py-0.5 text-xs">
                      {t('templateGenerate.teams.noRulesBadge')}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        {teamsTotal > teams.length && (
          <p className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            {t('templateGenerate.teams.truncated', {
              shown: teams.length,
              total: teamsTotal,
            })}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t('templateGenerate.teams.help')}
        </p>
        <p className="text-xs text-muted-foreground">
          <Link
            to={`/orgs/${encodeURIComponent(slug)}/settings/team-categories`}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {t('templateGenerate.teams.categoriesLink')}
          </Link>{' '}
          {t('templateGenerate.teams.categoriesHint')}
        </p>
      </fieldset>

      {locations.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="flex items-baseline justify-between gap-3 text-sm font-medium text-foreground">
            <span>{t('templateGenerate.locations.legend')}</span>
            <span className="flex items-center gap-3">
              <button
                type="button"
                onClick={toggleSelectAllLocations}
                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                {locations.every((loc) => selectedLocationIDs.has(loc.id))
                  ? t('templateGenerate.locations.deselectAll')
                  : t('templateGenerate.locations.selectAll')}
              </button>
              <span className="text-xs font-normal text-muted-foreground">
                {selectedLocationIDs.size === 0
                  ? t('templateGenerate.locations.allSelected')
                  : t('templateGenerate.locations.selectedCount', {
                      count: selectedLocationIDs.size,
                    })}
              </span>
            </span>
          </legend>
          <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto rounded-xl border border-border bg-muted/20 p-2 sm:grid-cols-2">
            {locations.map((loc) => (
              <label
                key={loc.id}
                className="flex cursor-pointer items-center gap-2 rounded-pill px-2 py-1.5 text-sm text-foreground hover:bg-background"
              >
                <Checkbox
                  checked={selectedLocationIDs.has(loc.id)}
                  onChange={() => toggleLocation(loc.id)}
                />
                <span className="truncate">{loc.name}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('templateGenerate.locations.help')}
          </p>
        </fieldset>
      )}
    </div>
  );
}

function ModePill({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex h-9 items-center gap-1.5 rounded-pill px-4 text-sm font-medium transition-transform active:scale-95 ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground hover:bg-muted/80')
      }
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function LoadingStep({
  progress,
  status,
}: {
  progress?: { batches_total: number; batches_done: number; slots_emitted: number } | undefined;
  status?: string | undefined;
}) {
  const { t } = useTranslation('common');
  const total = progress?.batches_total ?? 0;
  const done = progress?.batches_done ?? 0;
  const slots = progress?.slots_emitted ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  // "pending" status from the worker means the row is waiting to be
  // claimed (typically <5s). Show the same UI but no progress bar yet.
  const showBar = total > 0 && status !== 'pending';

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
      <CircleNotch
        className="h-10 w-10 animate-spin text-primary-foreground"
        weight="bold"
      />
      <p className="font-heading text-base font-semibold text-foreground">
        {t('templateGenerate.loading.heading')}
      </p>
      <p className="max-w-xs text-sm text-muted-foreground">
        {t('templateGenerate.loading.body')}
      </p>
      {showBar && (
        <div className="flex w-full max-w-sm flex-col gap-1.5">
          <div className="h-2 w-full overflow-hidden rounded-pill bg-muted">
            <div
              className="h-full rounded-pill bg-primary transition-[width] duration-300 ease-out"
              style={{ width: `${pct}%` }}
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('templateGenerate.loading.progress', {
              done,
              total,
              slots,
            })}
          </p>
        </div>
      )}
      <p className="max-w-xs text-xs text-muted-foreground">
        {t('templateGenerate.loading.backgroundHint')}
      </p>
    </div>
  );
}

function PreviewStep({
  draft,
}: {
  draft: TemplateDraft;
}) {
  const { t } = useTranslation('common');

  // Per-team summary is paged client-side so an org with hundreds of teams
  // doesn't render a wall of rows. Keep state local to the dialog — URL
  // search-params would pollute the underlying page's history.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const perTeam = draft.summary.per_team;
  const pageStart = (page - 1) * pageSize;
  const pageRows = perTeam.slice(pageStart, pageStart + pageSize);

  return (
    <div className="flex flex-col gap-5 text-foreground">
      <section>
        <h3 className="font-heading text-base font-semibold text-foreground">
          {t('templateGenerate.preview.summaryHeading')}
        </h3>
        <div className="mt-2 overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm text-foreground">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-heading font-semibold">
                  {t('templateGenerate.preview.col.team')}
                </th>
                <th className="px-3 py-2 text-right font-heading font-semibold">
                  {t('templateGenerate.preview.col.perWeek')}
                </th>
                <th className="px-3 py-2 text-right font-heading font-semibold">
                  {t('templateGenerate.preview.col.minutes')}
                </th>
                <th className="px-3 py-2 text-right font-heading font-semibold">
                  {t('templateGenerate.preview.col.location')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pageRows.map((row) => (
                <tr key={row.team_id}>
                  <td className="px-3 py-2 text-foreground">
                    <span
                      className={
                        row.included
                          ? 'text-foreground'
                          : 'text-muted-foreground line-through'
                      }
                    >
                      {row.team_name}
                    </span>
                    {!row.included && (
                      <span className="ml-2 rounded-pill bg-muted px-2 py-0.5 text-[0.7rem] font-medium text-muted-foreground">
                        {t(`templateGenerate.preview.skip.${row.skip_reason}`, {
                          defaultValue: row.skip_reason,
                        })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">
                    {row.included ? row.slot_count : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">
                    {row.included ? row.total_minutes : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">
                    {row.included
                      ? `${row.slots_with_location}/${row.slot_count || '—'}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination
          className="mt-2"
          total={perTeam.length}
          page={page}
          size={pageSize}
          pageSizes={[25, 50, 100]}
          onPageChange={setPage}
          onSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </section>

      {draft.summary.warnings.length > 0 && (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-3">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-amber-900">
            <Warning className="h-4 w-4" weight="bold" aria-hidden />
            {t('templateGenerate.preview.warningsHeading')}
          </h3>
          <ul className="mt-1.5 flex flex-col gap-1 text-sm text-amber-900">
            {draft.summary.warnings.map((w, i) => (
              <li key={i}>
                {w.team_name && <strong>{w.team_name}: </strong>}
                {w.detail}
              </li>
            ))}
          </ul>
        </section>
      )}

      {draft.summary.dropped_rules.length > 0 && (
        <section className="rounded-xl border border-border bg-muted/20 p-3">
          <h3 className="font-heading text-sm font-semibold text-foreground">
            {t('templateGenerate.preview.droppedRulesHeading')}
          </h3>
          <ul className="mt-1.5 flex flex-col gap-1 text-sm text-foreground">
            {draft.summary.dropped_rules.map((r, i) => (
              <li key={i}>
                <strong className="text-foreground">{r.team_name}</strong>{' '}
                <span className="rounded-pill bg-muted px-1.5 py-0.5 text-[0.7rem] font-medium text-muted-foreground">
                  {t(`templateGenerate.preview.strictness.${r.strictness}`, {
                    defaultValue: r.strictness,
                  })}
                </span>{' '}
                <span className="text-foreground">— {r.detail}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
